import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export interface BrandAsset {
  id: string;
  asset_id: string;
  display_name: string;
  category: string;
  subcategory: string | null;
  tags: string[];
  storage_path: string;
  format: string;
  file_size_kb: number | null;
  width: number | null;
  height: number | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface BrandAssetUpload {
  id: string;
  file_name: string;
  storage_path: string;
  file_size_mb: number | null;
  status: string;
  total_files: number | null;
  processed_files: number;
  error_message: string | null;
  uploaded_by: string | null;
  created_at: string;
  processed_at: string | null;
}

// Hook to fetch brand assets
export const useBrandAssets = (
  category?: string, 
  subcategory?: string,
  includeInactive = false
) => {
  return useQuery({
    queryKey: ["brand-assets", category, subcategory, includeInactive],
    queryFn: async () => {
      let query = supabase
        .from("brand_assets")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("display_name", { ascending: true });

      if (category) {
        query = query.eq("category", category);
      }

      if (subcategory) {
        query = query.eq("subcategory", subcategory);
      }

      if (!includeInactive) {
        query = query.eq("is_active", true);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as BrandAsset[];
    },
  });
};

// Hook to get unique subcategories
export const useBrandAssetSubcategories = (category?: string) => {
  return useQuery({
    queryKey: ["brand-asset-subcategories", category],
    queryFn: async () => {
      let query = supabase
        .from("brand_assets")
        .select("subcategory")
        .eq("is_active", true);

      if (category) {
        query = query.eq("category", category);
      }

      const { data, error } = await query;

      if (error) throw error;

      const subcategories = [...new Set(data.map((d) => d.subcategory).filter(Boolean))].sort();
      return subcategories as string[];
    },
  });
};

// Hook to fetch single asset by asset_id
export const useBrandAsset = (assetId: string) => {
  return useQuery({
    queryKey: ["brand-asset", assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_assets")
        .select("*")
        .eq("asset_id", assetId)
        .single();

      if (error) throw error;
      return data as BrandAsset;
    },
    enabled: !!assetId,
  });
};

// Hook to fetch uploads (admin only)
export const useBrandAssetUploads = () => {
  return useQuery({
    queryKey: ["brand-asset-uploads"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_asset_uploads")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as BrandAssetUpload[];
    },
  });
};

// Hook to get unique categories
export const useBrandAssetCategories = () => {
  return useQuery({
    queryKey: ["brand-asset-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_assets")
        .select("category")
        .eq("is_active", true);

      if (error) throw error;

      const categories = [...new Set(data.map((d) => d.category))].sort();
      return categories;
    },
  });
};

// Mutation to update asset
export const useUpdateBrandAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<BrandAsset>;
    }) => {
      const { data, error } = await supabase
        .from("brand_assets")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-assets"] });
      queryClient.invalidateQueries({ queryKey: ["brand-asset-categories"] });
    },
  });
};

// Mutation to delete asset
export const useDeleteBrandAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, storagePath }: { id: string; storagePath: string }) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("brand-assets")
        .remove([storagePath]);

      if (storageError) {
        logger.error("[useBrandAssets] Storage delete error:", storageError);
      }

      // Delete from catalog
      const { error } = await supabase
        .from("brand_assets")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-assets"] });
      queryClient.invalidateQueries({ queryKey: ["brand-asset-categories"] });
    },
  });
};

// Mutation to register upload
export const useRegisterBrandAssetUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fileName,
      storagePath,
      fileSizeMb,
    }: {
      fileName: string;
      storagePath: string;
      fileSizeMb: number;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("brand_asset_uploads")
        .insert({
          file_name: fileName,
          storage_path: storagePath,
          file_size_mb: fileSizeMb,
          status: "pending",
          uploaded_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as BrandAssetUpload;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-asset-uploads"] });
    },
  });
};

// Mutation to process upload (call edge function)
export const useProcessBrandAssetUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      uploadId,
      defaultCategory,
    }: {
      uploadId: string;
      defaultCategory?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke(
        "process-brand-assets",
        {
          body: { uploadId, defaultCategory },
        }
      );

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-asset-uploads"] });
      queryClient.invalidateQueries({ queryKey: ["brand-assets"] });
      queryClient.invalidateQueries({ queryKey: ["brand-asset-categories"] });
    },
  });
};

// Helper to get public URL for an asset
export const getBrandAssetUrl = (
  storagePath: string,
  options?: { width?: number; height?: number; quality?: number }
): string => {
  const { data } = supabase.storage.from("brand-assets").getPublicUrl(storagePath, {
    transform: options
      ? {
          width: options.width,
          height: options.height,
          quality: options.quality || 80,
        }
      : undefined,
  });

  return data.publicUrl;
};
